import { formatNumber } from "../../components/charts/formatters"
import {
  getPulsePreviousDate,
  socialNetworkTypeIdToLabel,
} from "./inboxHealthReport"
import {
  catalogTypeIdFromKey,
  formatNetNewFollowers,
  PULSE_PLATFORM_ICON_PATHS,
  socialNetworkTypeIdToDashboardPlatformKey,
  typeIdTotalsToPlatformBreakdown,
} from "./pulseCardReport"

/** Previous comparison date for daily performance API (2 days before today). */
export function getDailyPerformancePreviousDate(currentDate = new Date()) {
  return getPulsePreviousDate(currentDate)
}

function normalizeMetricName(metricName) {
  return String(metricName || "").trim().toLowerCase()
}

function metricCategory(metricName) {
  const n = normalizeMetricName(metricName)
  if (!n) return "other"
  if (n === "followers") return "followers"
  if (n === "new_post_engagement") return "engagement"
  if (n === "post_likes") return "likes"
  if (n === "post_comments") return "comments"
  if (n === "post_shares") return "shares"
  return "other"
}

function rowDelta(row) {
  const latest = Number(row?.latestValue) || 0
  const previous = Number(row?.previousValue) || 0
  return latest - previous
}

function sumByCategory(rows, category, field = "latest") {
  let total = 0
  for (const row of rows) {
    if (metricCategory(row?.metricName) !== category) continue
    if (field === "latest") total += Number(row?.latestValue) || 0
    else if (field === "previous") total += Number(row?.previousValue) || 0
    else total += rowDelta(row)
  }
  return total
}

/** Per-platform sum of new_post_engagement for latest or previous period. */
function platformEngagementTotals(rows, field = "latest") {
  const map = new Map()
  for (const row of rows) {
    if (metricCategory(row?.metricName) !== "engagement") continue
    const id = row?.socialNetworkTypeId
    const key = id != null ? String(id) : "unknown"
    const value =
      field === "previous"
        ? Number(row?.previousValue) || 0
        : Number(row?.latestValue) || 0
    map.set(key, (map.get(key) || 0) + value)
  }
  return map
}

function topPlatformIdFromTotals(totals) {
  let topPlatformId = null
  let topValue = -1
  totals.forEach((val, id) => {
    if (val > topValue) {
      topValue = val
      topPlatformId = id
    }
  })
  return topPlatformId
}

/** Per-platform sum of likes + comments + shares (latest period). */
function platformInteractionTotals(rows) {
  const map = new Map()
  for (const row of rows) {
    const cat = metricCategory(row?.metricName)
    if (cat !== "likes" && cat !== "comments" && cat !== "shares") continue
    const id = row?.socialNetworkTypeId
    const key = id != null ? String(id) : "unknown"
    map.set(key, (map.get(key) || 0) + (Number(row?.latestValue) || 0))
  }
  return map
}

function platformFollowerDeltas(rows) {
  const map = new Map()
  for (const row of rows) {
    if (metricCategory(row?.metricName) !== "followers") continue
    const id = row?.socialNetworkTypeId
    const key = id != null ? String(id) : "unknown"
    map.set(key, (map.get(key) || 0) + rowDelta(row))
  }
  return map
}

/** Per-platform follower count for latest or previous period. */
function platformFollowerTotals(rows, field = "latest") {
  const map = new Map()
  for (const row of rows) {
    if (metricCategory(row?.metricName) !== "followers") continue
    const id = row?.socialNetworkTypeId
    const key = id != null ? String(id) : "unknown"
    const value =
      field === "previous"
        ? Number(row?.previousValue) || 0
        : Number(row?.latestValue) || 0
    map.set(key, (map.get(key) || 0) + value)
  }
  return map
}

function percentChange(latest, previous) {
  if (previous === 0) return latest > 0 ? 100 : 0
  return ((latest - previous) / previous) * 100
}

function sparklineTrendFromValues(previous, latest) {
  if (latest > previous) return "up"
  if (latest < previous) return "down"
  return "stable"
}

function buildPlatformEngagementRowsFromDaily(rows, socialNetworkTypes = []) {
  const totalsLatest = platformEngagementTotals(rows, "latest")
  const interactionLatest = platformInteractionTotals(rows)
  const keys = new Set([...totalsLatest.keys(), ...interactionLatest.keys()])

  return Array.from(keys)
    .map((idKey) => {
      const typeId = idKey === "unknown" ? null : Number(idKey)
      const platformKey =
        typeId != null ? socialNetworkTypeIdToDashboardPlatformKey(typeId, socialNetworkTypes) : null
      if (!platformKey) return null
      const totalEngagements =
        totalsLatest.get(idKey) ||
        interactionLatest.get(idKey) ||
        0
      return {
        platform: platformKey,
        label: socialNetworkTypeIdToLabel(socialNetworkTypes, typeId),
        icon: PULSE_PLATFORM_ICON_PATHS[platformKey],
        totalEngagements,
        dayDelta: 0,
      }
    })
    .filter(Boolean)
    .sort((a, b) => b.totalEngagements - a.totalEngagements)
}

function buildPlatformFollowerRowsFromDaily(rows, socialNetworkTypes = []) {
  const followerLatest = platformFollowerTotals(rows, "latest")
  const followerDeltas = platformFollowerDeltas(rows)

  return Array.from(followerLatest.keys())
    .map((idKey) => {
      const typeId = Number(idKey)
      if (Number.isNaN(typeId)) return null
      const platformKey = socialNetworkTypeIdToDashboardPlatformKey(typeId, socialNetworkTypes)
      if (!platformKey) return null
      const netNew = followerDeltas.get(idKey) ?? 0
      const totalFollowers = followerLatest.get(idKey) ?? 0
      const previous = totalFollowers - netNew
      const growth = previous !== 0 ? netNew / previous : 0
      return {
        platform: platformKey,
        label: socialNetworkTypeIdToLabel(socialNetworkTypes, typeId),
        icon: PULSE_PLATFORM_ICON_PATHS[platformKey],
        totalFollowers,
        netNew,
        growth: Number.isFinite(growth) ? growth : 0,
      }
    })
    .filter(Boolean)
    .sort((a, b) => (b.growth ?? 0) - (a.growth ?? 0))
}

function ensureEngagementRowsForConnected(rows, socialNetworkTypes, connectedPlatformKeys) {
  const built = buildPlatformEngagementRowsFromDaily(rows, socialNetworkTypes)
  const seen = new Set(built.map((r) => r.platform))
  for (const platformKey of connectedPlatformKeys || []) {
    if (!platformKey || seen.has(platformKey)) continue
    const typeId = catalogTypeIdFromKey(socialNetworkTypes, platformKey)
    built.push({
      platform: platformKey,
      label:
        typeId != null
          ? socialNetworkTypeIdToLabel(socialNetworkTypes, typeId)
          : platformKey,
      icon: PULSE_PLATFORM_ICON_PATHS[platformKey],
      totalEngagements: 0,
      dayDelta: 0,
    })
  }
  return built.sort((a, b) => b.totalEngagements - a.totalEngagements)
}

function ensureFollowerRowsForConnected(rows, socialNetworkTypes, connectedPlatformKeys) {
  const built = buildPlatformFollowerRowsFromDaily(rows, socialNetworkTypes)
  const seen = new Set(built.map((r) => r.platform))
  for (const platformKey of connectedPlatformKeys || []) {
    if (!platformKey || seen.has(platformKey)) continue
    const typeId = catalogTypeIdFromKey(socialNetworkTypes, platformKey)
    built.push({
      platform: platformKey,
      label:
        typeId != null
          ? socialNetworkTypeIdToLabel(socialNetworkTypes, typeId)
          : platformKey,
      icon: PULSE_PLATFORM_ICON_PATHS[platformKey],
      totalFollowers: 0,
      netNew: 0,
      growth: 0,
    })
  }
  return built.sort((a, b) => (b.growth ?? 0) - (a.growth ?? 0))
}

const FOLLOWER_DETAIL_PLATFORMS = [
  { label: "Instagram Followers", match: /instagram/i },
  { label: "Facebook Followers", match: /facebook/i },
  { label: "LinkedIn Followers", match: /linkedin/i },
  { label: "X Followers", match: /twitter|^x$/i },
  { label: "TikTok Followers", match: /tiktok/i },
]

/**
 * @param {Array<{
 *   metricName?: string,
 *   latestValue?: number,
 *   previousValue?: number,
 *   socialNetworkTypeId?: string|number,
 * }>} rows
 */
export function buildEngagementCard(
  baseCard,
  rows,
  socialNetworkTypes = [],
  connectedPlatformKeys = [],
) {
  if (!baseCard) return baseCard

  const safeRows = Array.isArray(rows) ? rows : []
  const likesLatest = sumByCategory(safeRows, "likes", "latest")
  const commentsLatest = sumByCategory(safeRows, "comments", "latest")
  const sharesLatest = sumByCategory(safeRows, "shares", "latest")
  const likesPrevious = sumByCategory(safeRows, "likes", "previous")
  const commentsPrevious = sumByCategory(safeRows, "comments", "previous")
  const sharesPrevious = sumByCategory(safeRows, "shares", "previous")

  const engagementLatest = sumByCategory(safeRows, "engagement", "latest")
  const engagementPrevious = sumByCategory(safeRows, "engagement", "previous")
  const breakdownLatest = likesLatest + commentsLatest + sharesLatest
  const breakdownPrevious = likesPrevious + commentsPrevious + sharesPrevious
  const hasEngagementMetrics = safeRows.some(
    (row) => metricCategory(row?.metricName) === "engagement",
  )

  const totalLatest = hasEngagementMetrics ? engagementLatest : breakdownLatest
  const totalPrevious = hasEngagementMetrics ? engagementPrevious : breakdownPrevious
  const pct = percentChange(totalLatest, totalPrevious)

  const platformTotalsLatest = platformEngagementTotals(safeRows, "latest")
  const platformTotalsPrevious = platformEngagementTotals(safeRows, "previous")
  const platformTotalsForTop = hasEngagementMetrics
    ? (totalLatest > 0 ? platformTotalsLatest : platformTotalsPrevious)
    : platformInteractionTotals(safeRows)
  const platformEngagementRows = ensureEngagementRowsForConnected(
    safeRows,
    socialNetworkTypes,
    connectedPlatformKeys,
  )
  const topEngagementRow = platformEngagementRows.find((row) => row.totalEngagements > 0)
  const topPlatform = topEngagementRow?.label ?? "—"

  return {
    ...baseCard,
    subLabel: baseCard.subLabel || "Total actions",
    value: formatNumber(totalLatest),
    trophyInsight:
      topPlatform !== "—"
        ? `Most interactions came from ${topPlatform}.`
        : "No platform interactions in this period.",
    platformBreakdown: typeIdTotalsToPlatformBreakdown(platformTotalsLatest, socialNetworkTypes),
    sparklineData: [totalPrevious, totalLatest],
    sparklineTrend: sparklineTrendFromValues(totalPrevious, totalLatest),
    sparklinePercentChange: pct,
    platformEngagementRows,
    detailRows: [
      { label: "Likes", value: formatNumber(likesLatest) },
      { label: "Comments", value: formatNumber(commentsLatest) },
      { label: "Shares", value: formatNumber(sharesLatest) },
    ],
  }
}

export function buildGrowthCard(
  baseCard,
  rows,
  socialNetworkTypes = [],
  connectedPlatformKeys = [],
) {
  if (!baseCard) return baseCard

  const safeRows = Array.isArray(rows) ? rows : []
  const followerDeltas = platformFollowerDeltas(safeRows)
  const followerLatestByPlatform = platformFollowerTotals(safeRows, "latest")
  let netFollowers = 0
  followerDeltas.forEach((delta) => {
    netFollowers += delta
  })

  let previousNet = 0
  let latestNet = 0
  for (const row of safeRows) {
    if (metricCategory(row?.metricName) !== "followers") continue
    previousNet += Number(row?.previousValue) || 0
    latestNet += Number(row?.latestValue) || 0
  }

  let topPlatformId = null
  let topDelta = -Infinity
  followerDeltas.forEach((delta, id) => {
    if (delta > topDelta) {
      topDelta = delta
      topPlatformId = id
    }
  })
  const topPlatform =
    netFollowers !== 0 && topPlatformId != null
      ? socialNetworkTypeIdToLabel(socialNetworkTypes, topPlatformId)
      : "—"

  const detailRows = FOLLOWER_DETAIL_PLATFORMS.map(({ label, match }) => {
    let count = 0
    followerLatestByPlatform.forEach((val, platformId) => {
      const name = socialNetworkTypeIdToLabel(socialNetworkTypes, platformId)
      if (match.test(name)) count += val
    })
    return { label, value: formatNumber(count) }
  })

  detailRows.push({ label: "Posts", value: formatNumber(0) })

  const platformBreakdown = typeIdTotalsToPlatformBreakdown(followerDeltas, socialNetworkTypes)

  return {
    ...baseCard,
    value: formatNumber(latestNet),
    secondaryValue: formatNetNewFollowers(netFollowers),
    netGrowthToday: netFollowers,
    trophyInsight:
      netFollowers !== 0 && topPlatform !== "—"
        ? `${topPlatform} drove the most follower gains.`
        : latestNet > 0
          ? `${formatNumber(latestNet)} total followers across connected platforms.`
          : "No follower change in this period.",
    platformBreakdown,
    sparklineData: [previousNet, latestNet],
    sparklineTrend: sparklineTrendFromValues(previousNet, latestNet),
    sparklinePercentChange: null,
    platformFollowerRows: ensureFollowerRowsForConnected(
      safeRows,
      socialNetworkTypes,
      connectedPlatformKeys,
    ),
    detailRows,
  }
}
